import { useCallback, useEffect, useState } from 'react';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';

/**
 * Hook for registration undo/redo operations.
 * Subscribes to the RegistrationUndoService and provides undo/redo callbacks.
 * Maintains separate undo/redo state per registration, the one of the
 * primary row's fused pair.
 */
export function useRegistrationUndoRedo() {
    const { environment } = useActiveFusionTarget();
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const registrationId = environment?.registration?.sopInstanceUid;

    useEffect(() => {
        const service = getRegistrationUndoService();

        const updateState = () => {
            if (registrationId) {
                setCanUndo(service.canUndo(registrationId));
                setCanRedo(service.canRedo(registrationId));
            } else {
                setCanUndo(false);
                setCanRedo(false);
            }
        };

        const subscription = service.subscribe(updateState);
        return () => subscription.unsubscribe();
    }, [registrationId]);

    const undo = useCallback(() => {
        if (!environment?.registration || !registrationId) {
            return;
        }

        const service = getRegistrationUndoService();
        const currentMatrix = environment.registration.transformation;
        const previousMatrix = service.undo(registrationId, currentMatrix);

        if (previousMatrix) {
            environment.setRegistrationMatrix(previousMatrix);
            environment.registration.syncTransformationToDataset();
        }
    }, [environment, registrationId]);

    const redo = useCallback(() => {
        if (!environment?.registration || !registrationId) {
            return;
        }

        const service = getRegistrationUndoService();
        const currentMatrix = environment.registration.transformation;
        const nextMatrix = service.redo(registrationId, currentMatrix);

        if (nextMatrix) {
            environment.setRegistrationMatrix(nextMatrix);
            environment.registration.syncTransformationToDataset();
        }
    }, [environment, registrationId]);

    return { canUndo, canRedo, undo, redo };
}
